import { SegmentationType, TransactionType } from '../../../@types/types'

export const DEFAULT_METADATA = {
  backgroundImage: '//www.static-immobilienscout24.de/statpic/default/12e92082442dd758e5e2b81c282555d2_default.jpg',
  headerHeight: 155,
  pointsTitle: undefined as string | undefined,
  points: [
    {
      text1Key: 'segmentation.boxInfo.points.digitalProcess.text1',
      text2Key: 'segmentation.boxInfo.points.digitalProcess.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.points.automaticSelection.text1',
      text2Key: 'segmentation.boxInfo.points.automaticSelection.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.points.performanceTracking.text1',
      text2Key: 'segmentation.boxInfo.points.performanceTracking.text2',
    },
  ],
  securityNoteKey: 'segmentation.boxInfo.securityNote',
}

export type SegmentationMetadataConfig = {
  backgroundImage: string
  headerHeight: number
  pointsTitle?: string
  pointsTitleClassName?: string
  points: Array<{ text1Key?: string; text2Key: string; noBullet?: boolean }>
  securityNoteKey?: string
  securityLink?: {
    href: string
    labelKey: string
  }
}

// Images for different segmentation types and transactions
const PROPERTY_OWNER_RENT_METADATA: SegmentationMetadataConfig = {
  backgroundImage: '//www.static-immobilienscout24.de/statpic/ho-rent/417c3e7a2c549907b50089db6e5e1fa9_ho-rent.jpg',
  headerHeight: 183,
  pointsTitle: 'segmentation.boxInfo.propertyOwnerRent.pointsTitle',
  points: [
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerRent.points.freeAd.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerRent.points.freeAd.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerRent.points.qualifiedTenants.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerRent.points.qualifiedTenants.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerRent.points.afterRental.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerRent.points.afterRental.text2',
    },
  ],
}

const PROPERTY_OWNER_SELL_METADATA: SegmentationMetadataConfig = {
  backgroundImage: '//www.static-immobilienscout24.de/statpic/ho-sell/d465186d9aeb6a83d36fde56423f0359_ho-sell.jpg',
  headerHeight: 183,
  pointsTitle: 'segmentation.boxInfo.propertyOwnerSell.pointsTitle',
  points: [
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerSell.points.fromValuation.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerSell.points.fromValuation.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerSell.points.maxVisibility.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerSell.points.maxVisibility.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerSell.points.afterSale.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerSell.points.afterSale.text2',
    },
  ],
}

const PROPERTY_OWNER_SHORT_TERM_METADATA: SegmentationMetadataConfig = {
  backgroundImage:
    '//www.static-immobilienscout24.de/statpic/ho-short-term/df39aa2e673ac5fc418d5af7dfcfee75_ho-short-term.jpg',
  headerHeight: 183,
  pointsTitle: 'segmentation.boxInfo.propertyOwnerShortTerm.pointsTitle',
  points: [
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerShortTerm.points.freeAd.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerShortTerm.points.freeAd.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerShortTerm.points.qualifiedTenants.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerShortTerm.points.qualifiedTenants.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.propertyOwnerShortTerm.points.afterRental.text1',
      text2Key: 'segmentation.boxInfo.propertyOwnerShortTerm.points.afterRental.text2',
    },
  ],
}

const TENANT_METADATA: SegmentationMetadataConfig = {
  backgroundImage: '//www.static-immobilienscout24.de/statpic/tenant/9da6e672d379273d0c3c722251ab1905_tenant.jpg',
  headerHeight: 183,
  pointsTitle: 'segmentation.boxInfo.tenant.pointsTitle',
  points: [
    {
      text1Key: 'segmentation.boxInfo.tenant.points.freeAdToHandover.text1',
      text2Key: 'segmentation.boxInfo.tenant.points.freeAdToHandover.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.tenant.points.findSuccessors.text1',
      text2Key: 'segmentation.boxInfo.tenant.points.findSuccessors.text2',
    },
    {
      text1Key: 'segmentation.boxInfo.tenant.points.afterPlacement.text1',
      text2Key: 'segmentation.boxInfo.tenant.points.afterPlacement.text2',
    },
  ],
}

const AGENT_METADATA: SegmentationMetadataConfig = {
  backgroundImage: 'https://www.static-immobilienscout24.de/statpic/profi/f94aed33962ce63f529de1965f8093a6_profi.jpg',
  headerHeight: 160,
  pointsTitle: 'segmentation.boxInfo.agent.pointsTitle',
  pointsTitleClassName: 'font-heading-xsmall-bold',
  points: [
    {
      text2Key: 'segmentation.boxInfo.agent.points.introText.text2',
      noBullet: true,
    },
  ],
  securityLink: {
    href: 'https://www.immobilienscout24.de/lp/neue-mitgliedschaften-2024-ppa.html?cmp_id=10-052566&cmp_name=new_memberships_lp&cmp_position=ppa_segmentation_page&cmp_creative=segmentation_wizard',
    labelKey: 'segmentation.boxInfo.agent.securityLink',
  },
}

export const getMetadataConfig = (
  type?: SegmentationType | null,
  transaction?: TransactionType | 'SHORT_TERM_ACCOMMODATION' | null
): SegmentationMetadataConfig => {
  // PROPERTY_OWNER with different transactions
  if (type === SegmentationType.PROPERTY_OWNER && transaction) {
    if (transaction === TransactionType.RENT) {
      return PROPERTY_OWNER_RENT_METADATA
    }
    if (transaction === TransactionType.SELL) {
      return PROPERTY_OWNER_SELL_METADATA
    }
    if (transaction === 'SHORT_TERM_ACCOMMODATION') {
      return PROPERTY_OWNER_SHORT_TERM_METADATA
    }
  }

  // TENANT - only show specific metadata if transaction is selected
  if (type === SegmentationType.TENANT && transaction) {
    return TENANT_METADATA
  }

  // AGENT - show specific metadata as soon as AGENT is selected (no transaction required)
  if (type === SegmentationType.AGENT) {
    return AGENT_METADATA
  }

  // Fallback to default (including when only segmentation is selected without transaction)
  return DEFAULT_METADATA
}
